/**
 * Downscales and re-encodes a photo in the browser before it is attached to an upload.
 *
 * A phone camera writes 2–8MB per frame, and a driver uploads several of them in one multipart
 * request. Re-encoding to a bounded JPEG turns that into a few hundred kilobytes per photo, which
 * is what keeps the licence/insurance submission inside the 5MB-per-file limit and off a mobile
 * connection long enough to time out.
 *
 * It also normalises the container: Java's ImageIO — which collates the two licence sides
 * server-side — reads JPEG/PNG/GIF/BMP and nothing else, so a HEIC frame straight off an iPhone
 * cannot be read there. Anything the browser can decode leaves here as JPEG.
 */

/** Longest edge kept after downscaling. A licence or pink slip stays legible well below this. */
export const MAX_IMAGE_DIMENSION = 2000;

/** JPEG qualities tried in order until the encoded photo fits TARGET_IMAGE_BYTES. */
const QUALITY_LADDER = [0.82, 0.7, 0.6];

/** Re-encoding stops once a photo is this small; well inside MAX_UPLOAD_FILE_SIZE. */
const TARGET_IMAGE_BYTES = 1.5 * 1024 * 1024;

/** Below this an already-web-safe photo is sent untouched rather than re-encoded lossily. */
const SKIP_COMPRESSION_BYTES = 400 * 1024;

/** Ceiling on the <img> decode fallback, which has no guarantee of ever firing a handler. */
const DECODE_TIMEOUT_MS = 15000;

/**
 * Types that may be sent untouched when they are already small.
 *
 * PNG only, and both exclusions are deliberate. WebP is absent because the server-side reader
 * cannot open one, however small it is. JPEG is absent because it is the format that carries
 * rotation as EXIF metadata rather than in the pixels, and the server-side reader ignores EXIF —
 * skipping the re-encode for a small portrait JPEG is exactly how a licence ends up stored
 * sideways, which is not a failure the driver can see or diagnose.
 */
const ALREADY_WEB_SAFE_TYPES = ['image/png'];

/**
 * Types that cannot carry an EXIF rotation, and so are safe to hand back without re-encoding.
 * JPEG and TIFF are both absent precisely because they can.
 */
const ROTATION_FREE_TYPES = ['image/png', 'image/gif', 'image/bmp'];

/**
 * Image types the server can actually decode. A file that reaches the server as anything else —
 * because the browser could not decode it either, so re-encoding was skipped — is refused up front
 * rather than uploaded and silently dropped during collation.
 *
 * The server enforces the same set in `utils.DocumentImageTypes.isReadable`; this
 * copy exists so a driver can be told what to change instead of getting a rejection from the API.
 * Keep the two in step.
 */
export const SERVER_READABLE_IMAGE_TYPES = [
  'image/jpeg', 'image/jpg', 'image/pjpeg',
  'image/png', 'image/x-png',
  'image/gif', 'image/bmp',
  // TIFF matters on the fall-through path specifically: Chrome and Firefox cannot decode one, so a
  // scanned licence comes back here untouched — and the server reads it perfectly well.
  'image/tiff', 'image/tif',
];

/**
 * Reports whether a picked file is an image this module will try to re-encode.
 *
 * @param {File} file the picked file
 * @returns {boolean} true for images; false for PDFs and for nothing at all
 */
export function isCompressibleImage(file) {
  return !!file && typeof file.type === 'string' && file.type.startsWith('image/');
}

/**
 * Reports whether the server can decode this file as an image.
 *
 * @param {File} file the picked file
 * @returns {boolean} true when the type is one the server-side image reader supports
 */
export function isServerReadableImage(file) {
  return !!file && SERVER_READABLE_IMAGE_TYPES.includes(file.type);
}

/**
 * Downscales a photo and re-encodes it as JPEG, returning a replacement {@link File}.
 *
 * Never throws and never rejects: a file the browser cannot decode is returned unchanged, so the
 * caller's own type and size checks stay the single place a bad upload is refused.
 *
 * @param {File} file the picked file
 * @param {number} [maxDimension] longest edge to keep, defaulting to {@link MAX_IMAGE_DIMENSION}
 * @returns {Promise<File>} the re-encoded photo, or the original file when it was left alone
 */
export async function prepareImageForUpload(file, maxDimension = MAX_IMAGE_DIMENSION) {
  if (!isCompressibleImage(file)) return file;
  if (file.size <= SKIP_COMPRESSION_BYTES && ALREADY_WEB_SAFE_TYPES.includes(file.type)) return file;

  let canvas = null;
  try {
    const source = await decodeImage(file);
    // naturalWidth/Height are the EXIF-corrected intrinsic dimensions.
    const {width, height} =
        scaledDimensions(source.naturalWidth, source.naturalHeight, maxDimension);

    canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    // JPEG has no alpha channel, so a transparent PNG would otherwise composite onto black.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(source, 0, 0, width, height);

    const blob = await encodeSmallEnough(canvas);
    if (!blob) return file;

    // Flat graphics can encode larger as JPEG than they were as PNG, and keeping the original is
    // worth it when it is already small enough to upload. Restricted to formats that carry no EXIF
    // rotation, though: handing back an untouched JPEG is the one case that would reintroduce the
    // sideways-licence problem this module exists to solve.
    if (blob.size >= file.size
        && ROTATION_FREE_TYPES.includes(file.type)
        && file.size <= TARGET_IMAGE_BYTES) {
      return file;
    }

    return new File([blob], jpegName(file.name), {type: 'image/jpeg', lastModified: Date.now()});
  } catch (e) {
    return file;
  } finally {
    // iOS Safari caps total canvas backing store per page, and four vehicle photos in a row is
    // enough to reach it — past which toBlob starts returning null and photos silently upload
    // full size. Releasing eagerly rather than waiting for GC keeps that out of reach.
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}

/**
 * Decodes a file into a drawable image with its EXIF rotation applied.
 *
 * Deliberately an <img> rather than createImageBitmap. A phone writes rotation as EXIF metadata and
 * the server-side reader ignores EXIF, so the rotation has to be baked into the pixels here — but
 * createImageBitmap's `imageOrientation` is a WebIDL dictionary member, which an engine that does
 * not implement it *ignores* rather than rejecting. That failure is silent: the call resolves, no
 * catch fires, and the licence is stored sideways. An <img> is oriented by the `image-orientation:
 * from-image` CSS default on every browser this app supports, with nothing to feature-detect.
 *
 * @param {File} file the picked file
 * @returns {Promise<HTMLImageElement>} the decoded, correctly oriented image
 */
async function decodeImage(file) {
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      // Bounded because neither handler is guaranteed to fire — a stalled decode would otherwise
      // leave this promise pending forever, and with it the caller's spinner and disabled input,
      // recoverable only by reloading the page and losing the whole wizard.
      const timer = setTimeout(() => reject(new Error('Image decode timed out')), DECODE_TIMEOUT_MS);
      img.onload = () => {
        clearTimeout(timer);
        resolve(img);
      };
      img.onerror = () => {
        clearTimeout(timer);
        reject(new Error('Image could not be decoded'));
      };
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Encodes the canvas at descending quality until the result fits the byte target.
 *
 * @param {HTMLCanvasElement} canvas the canvas holding the scaled photo
 * @returns {Promise<Blob|null>} the encoded JPEG, or null when the browser refused to encode
 */
async function encodeSmallEnough(canvas) {
  let smallest = null;
  for (const quality of QUALITY_LADDER) {
    const blob = await toBlob(canvas, quality);
    if (!blob) return smallest;
    smallest = blob;
    if (blob.size <= TARGET_IMAGE_BYTES) return blob;
  }
  return smallest;
}

/**
 * Promisified canvas.toBlob.
 *
 * @param {HTMLCanvasElement} canvas the canvas to encode
 * @param {number} quality JPEG quality between 0 and 1
 * @returns {Promise<Blob|null>} the encoded blob, or null when encoding is unsupported
 */
function toBlob(canvas, quality) {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
    } catch (e) {
      // Resolving rather than rejecting so the documented "null when unsupported" contract holds
      // for any caller, not just the one that happens to sit inside a try/catch.
      resolve(null);
    }
  });
}

/**
 * Scales a width/height pair down so the longest edge is at most maxDimension, leaving smaller
 * images at their original size.
 *
 * @param {number} width the source width in pixels
 * @param {number} height the source height in pixels
 * @param {number} maxDimension the longest edge to keep
 * @returns {{width: number, height: number}} the scaled dimensions, never below 1px
 */
function scaledDimensions(width, height, maxDimension) {
  const longest = Math.max(width, height);
  if (longest <= maxDimension) return {width, height};
  const ratio = maxDimension / longest;
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

/**
 * Rewrites a filename to carry a .jpg extension, so the stored document's name matches its bytes.
 *
 * @param {string} name the original filename
 * @returns {string} the filename with a .jpg extension
 */
function jpegName(name) {
  const base = (name || 'photo').replace(/\.[^.]+$/, '');
  return `${base || 'photo'}.jpg`;
}
